import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { eq } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "@/db/client";
import { getLucia, type UserRole } from "@/auth/lucia";
import { verifyPassword } from "@/auth/password";

export interface CurrentUser {
  id: string;
  email: string;
  role: UserRole;
}

export const loginFn = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        email: z.string().email().max(320),
        password: z.string().min(1).max(1024),
      })
      .parse(data),
  )
  .handler(async ({ data }): Promise<{ ok: true; user: CurrentUser } | { ok: false; error: string }> => {
    const email = data.email.trim().toLowerCase();
    const db = getDb();
    const [row] = await db
      .select({
        id: schema.appUser.id,
        email: schema.appUser.email,
        role: schema.appUser.role,
        passwordHash: schema.appUser.passwordHash,
      })
      .from(schema.appUser)
      .where(eq(schema.appUser.email, email))
      .limit(1);

    if (!row) {
      // выравниваем тайминг: тратим argon2 verify против фиктивного hash, чтобы
      // ответ при неверном email шёл столько же, сколько при неверном пароле.
      await verifyPassword(
        "$argon2id$v=19$m=19456,t=2,p=1$ZmFrZWZha2VmYWtlZmFrZQ$ZmFrZWZha2VmYWtlZmFrZWZha2VmYWtlZmFrZWZha2U",
        data.password,
      );
      return { ok: false, error: "Неверный email или пароль" };
    }

    const ok = await verifyPassword(row.passwordHash, data.password);
    if (!ok) {
      return { ok: false, error: "Неверный email или пароль" };
    }

    const lucia = getLucia();
    const session = await lucia.createSession(row.id, {});
    const cookie = lucia.createSessionCookie(session.id);
    setCookie(cookie.name, cookie.value, cookie.attributes);

    return {
      ok: true,
      user: { id: row.id, email: row.email, role: row.role as UserRole },
    };
  });

export const logoutFn = createServerFn({ method: "POST" }).handler(async (): Promise<{ ok: true }> => {
  const lucia = getLucia();
  const sessionId = getCookie(lucia.sessionCookieName);
  if (sessionId) {
    await lucia.invalidateSession(sessionId);
  }
  const blank = lucia.createBlankSessionCookie();
  setCookie(blank.name, blank.value, blank.attributes);
  return { ok: true };
});

export const getCurrentUserFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<CurrentUser | null> => {
    const lucia = getLucia();
    const sessionId = getCookie(lucia.sessionCookieName);
    if (!sessionId) return null;

    const { session, user } = await lucia.validateSession(sessionId);
    if (!session) {
      const blank = lucia.createBlankSessionCookie();
      setCookie(blank.name, blank.value, blank.attributes);
      return null;
    }
    if (session.fresh) {
      const refreshed = lucia.createSessionCookie(session.id);
      setCookie(refreshed.name, refreshed.value, refreshed.attributes);
    }
    return { id: user.id, email: user.email, role: user.role };
  },
);
